import { Portals } from '@backoffice-frontend/common'
import {
  currentEnvironmentNotBuildEnvironment,
  getEnvironment,
  isLocalhost,
} from '@backoffice-frontend/environment'
import {
  Features,
  useIsFeatureToggleEnabled,
} from '@backoffice-frontend/feature-toggle'
import {
  backroundEnv,
  getEnvLabel,
  useMediaQuery,
  useNavigationDrawerState,
  useSetNavigationDrawerState,
  useTheme,
} from '@backoffice-frontend/patterns'
import type { ReactNode } from 'react'
import type { SectionsConfig } from '../types/NavigationSectionsConfig'
import { MobileFilters } from './Mobile/MobileFilters'
import { MobileHeader } from './Mobile/MobileHeader'
import { NavigationDrawerContent } from './NavigationDrawerContent'
import { NavigationDrawerFooter } from './NavigationDrawerFooter'
import { NavigationDrawerLogo } from './NavigationDrawerLogo'

type NavigationDrawerProps = {
  navigationSections: SectionsConfig
  logo: ReactNode
  logoVariant?: 'dark' | 'light'
}

export const NavigationDrawer = ({
  navigationSections,
  logo,
  logoVariant,
}: NavigationDrawerProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const open = useNavigationDrawerState()
  const setOpen = useSetNavigationDrawerState()
  const alwaysShowEnvironmentIndicator = useIsFeatureToggleEnabled(
    Features.ALWAYS_SHOW_ENVIRONMENT_INDICATOR,
  )

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div
      css={{
        background: theme.semantic.ColorSurfaceDefault,
        boxShadow: theme.shadows[2],
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {(isLocalhost ||
        alwaysShowEnvironmentIndicator ||
        currentEnvironmentNotBuildEnvironment()) && (
        <span
          css={theme => ({
            height: theme.space.Space6,
            padding: `${theme.space.Space1} 0`,
            background: `linear-gradient(90deg, ${backroundEnv[getEnvironment()].dark} 0%, ${backroundEnv[getEnvironment()].bright} 100%)`,
            color:
              theme.semantic
                .ColorContentOnContrastCriticalSuccessDefaultAlternative,
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            letterSpacing: '0.75px',
          })}
        >
          {open
            ? getEnvironment().toUpperCase()
            : getEnvLabel(getEnvironment())}
        </span>
      )}
      {isMobile ? (
        <>
          <MobileHeader
            onClose={handleClose}
            logo={logo}
            logoVariant={logoVariant}
          />
          <div id={Portals.DedicatedDropdown} />
          <NavigationDrawerContent
            filters={<MobileFilters />}
            open={open}
            navigationSections={navigationSections}
            onAreaChange={handleClose}
          />
        </>
      ) : (
        <>
          <NavigationDrawerLogo
            open={open}
            logo={logo}
            logoVariant={logoVariant}
          />
          <NavigationDrawerContent
            open={open}
            navigationSections={navigationSections}
          />
          <NavigationDrawerFooter onClick={() => setOpen(!open)} open={open} />
        </>
      )}
    </div>
  )
}
